import { css } from 'aphrodite';
import { parseAsString, useQueryState } from 'nuqs';
import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useCoachingConversationCallView } from '../../../queries/AIConversation/useCoachingConversationCallView';
import {
	AppDrawerContent,
	AppDrawerToolbarButton,
	CoffeeDrawerProvider,
	CoffeeDrawerToolbar,
	SideDrawer,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import '../../focused-view/theme/tokens.css';
import { AdherenceDrawerContent } from './AdherenceDrawerContent';
import { AICoachingReviewTopBar } from './AICoachingReviewTopBar';
import { ConversationInsights } from './ConversationInsights';
import { ConversationList } from './ConversationList';
import { MetadataPanel } from './MetadataPanel';
import { styleSheet } from './styles';
import { TranscriptPanel } from './TranscriptPanel';

const DRAWER_VALUE_ADHERENCE = 'ai-coaching-review-adherence';

const AdherenceToolbarIcon = () => (
	<svg
		width='16'
		height='16'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<path d='M9 4h6a1 1 0 0 1 1 1v1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h1V5a1 1 0 0 1 1-1z' />
		<path d='M9 13l2 2 4-4' />
	</svg>
);

/**
 * Admin-gated Coffee page for reviewing an account's SalesLiveCoaching AI conversations. The left
 * panel lists the account's coaching conversations (across all reps) and tracks the selected one;
 * selecting a conversation populates the metadata, transcript, and insights panels on the right, and
 * the coaching-adherence breakdown in a right-side drawer toggled from the edge toolbar.
 *
 * Access: the `/ai-coaching-review` route is gated on `userSession.account.isAdmin` (see
 * `AidaRouter`), so it is available to admins/managers of the current account — not restricted to
 * Levitate-internal accounts. The page is account-scoped: it only ever surfaces the viewing
 * account's own conversations.
 */
export function CoffeeAICoachingReview() {
	// Selection lives in the URL (`?convo=<id>`) via nuqs so a conversation is deep-linkable and the
	// route reflects what's open. `null` clears the param (nothing selected). The aida app mounts the
	// react-router-v5 nuqs adapter, so this reads/writes the hash query string.
	const [selectedConversationId, setSelectedConversationId] = useQueryState('convo', parseAsString);
	const [drawerValue, setDrawerValue] = useState('');
	const isMobile = useMediaQuery({ maxWidth: THEME.breakpoints.md });
	const { hasManagerPermissions } = useManagerPermissions();

	// Deep-link safety: a `?convo=<id>` that resolves to nothing (deleted / not a coaching call) 404s
	// on the by-id fetch — surface it as a single not-found message rather than empty panels.
	const { notFound } = useCoachingConversationCallView(selectedConversationId);

	const showList = !isMobile || !selectedConversationId;
	const showDetail = !isMobile || Boolean(selectedConversationId);
	// The adherence drawer is a docked right rail; only offer it to managers/admins and on non-mobile
	// widths where the rail has room to open alongside the detail column.
	const showAdherenceDrawer = !isMobile && hasManagerPermissions;

	return (
		<div data-focused-view className={`focused-view-root ${css(styleSheet.page)}`}>
			<AICoachingReviewTopBar />
			<CoffeeDrawerProvider value={drawerValue} onValueChange={setDrawerValue}>
				<div className={css(styleSheet.layout)}>
					{showList ? (
						<section className={css(styleSheet.panel, styleSheet.listPanel)}>
							<div className={css(styleSheet.panelHeader)}>
								<h2 className={css(styleSheet.panelTitle)}>AI Coaching Conversations</h2>
							</div>
							<ConversationList
								selectedConversationId={selectedConversationId}
								onSelect={id => setSelectedConversationId(id)}
							/>
						</section>
					) : null}

					{showDetail ? (
						<section className={css(styleSheet.panel, styleSheet.detailPanel)}>
							{notFound ? (
								<div className={css(styleSheet.detailNotFound)}>
									This conversation could not be found. It may have been deleted, or the link is invalid.
								</div>
							) : (
								<>
									<MetadataPanel conversationId={selectedConversationId} />
									<ConversationInsights conversationId={selectedConversationId} />
									<TranscriptPanel conversationId={selectedConversationId} />
								</>
							)}
						</section>
					) : null}

					{showAdherenceDrawer ? (
						<SideDrawer>
							<AppDrawerContent value={DRAWER_VALUE_ADHERENCE} unmountOnExit>
								<AdherenceDrawerContent conversationId={selectedConversationId} onClose={() => setDrawerValue('')} />
							</AppDrawerContent>
							<CoffeeDrawerToolbar>
								<AppDrawerToolbarButton value={DRAWER_VALUE_ADHERENCE} tooltip='Coaching adherence'>
									<AdherenceToolbarIcon />
								</AppDrawerToolbarButton>
							</CoffeeDrawerToolbar>
						</SideDrawer>
					) : null}
				</div>
			</CoffeeDrawerProvider>
		</div>
	);
}
